import { LDateTime } from '@app/components/date/LDateTime';
import { UserLabel } from '@app/components/users/UserLabel';
import { PlaybackGuard } from '@app/core/playback/context/PlaybackGuard';
import { FragmentDuration } from '@app/modules/fragments/components/type/FragmentDuration';
import { DateTimeStr } from '@mi-front/core';
import { LocaleRelativeTime } from '@mi-front/ui';
import { Box, Card, CardContent, Stack, Tooltip } from '@mui/material';
import { useFragment } from '../../hooks/useFragment';
import { FragmentDescription } from './FragmentDescription';
import { FragmentGoToStreamFromPlaybackAction } from './actions/FragmentGoToStreamFromPlaybackAction';
import { FragmentGoToStreamFromStartAction } from './actions/FragmentGoToStreamFromStartAction';
import { FragmentGoToStreamWithSelectionAction } from './actions/FragmentGoToStreamWithSelectionAction';
import { FragmentMetadataTags } from './FragmentTags';

/**
 * Displays fragment metadata (date, duration, creator, description, tags) in the fragment view.
 */
export function FragmentMetadata() {
  const { fragment, station } = useFragment();
  const description = fragment.description?.trim();

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Stack
            direction="row"
            flexWrap="wrap"
            gap={1}
            alignItems="center"
            sx={{ typography: 'body2', color: 'text.secondary' }}
          >
            <LDateTime value={fragment.from as DateTimeStr} showRelative="inline" />
            <Box component="span">•</Box>
            <FragmentDuration fragment={fragment} />
            <Box component="span">•</Box>
            <PlaybackGuard>
              <FragmentGoToStreamWithSelectionAction
                stationSlug={station.slug}
                fragmentFrom={fragment.from as DateTimeStr}
                fragmentTo={fragment.to as DateTimeStr}
              />
              <FragmentGoToStreamFromStartAction
                stationSlug={station.slug}
                fragmentFrom={fragment.from as DateTimeStr}
              />
              <FragmentGoToStreamFromPlaybackAction stationSlug={station.slug} />
            </PlaybackGuard>
          </Stack>
          {fragment.creator ? (
            <Tooltip title={<LocaleRelativeTime value={fragment.createdAt} />} placement="top">
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  typography: 'body2',
                  color: 'text.secondary',
                  flexShrink: 0,
                }}
              >
                <UserLabel
                  showIcon
                  id={fragment.creator.id}
                  user={{
                    name: fragment.creator.name ?? '',
                    surname: fragment.creator.surname ?? '',
                    email: fragment.creator.email ?? '',
                  }}
                />
              </Box>
            </Tooltip>
          ) : null}
        </Box>
        {!!fragment.tags?.length && (
          <Box sx={{ mt: 1, typography: 'body2' }}>
            <FragmentMetadataTags stationSlug={station.slug} tags={fragment.tags} />
          </Box>
        )}
        {description && <FragmentDescription description={description} />}
      </CardContent>
    </Card>
  );
}
